import { useCallback, useState } from "react";

import Link from "@whoppah/next/link";
import { Locale, useRouter } from "next/router";

import { useHeaderLanguageSwitcherContext } from "./HeaderLanguageSwitcherContext";
import { useAuth } from "@/auth";
import { useLocaleLinks } from "@/common/hooks/intl.hooks";
import { setLocaleCookie } from "@/common/utils/cookie.utils";
import { cn } from "@/common/utils/css.utils";
import { getLanguageName } from "@/common/utils/intl.utils";
import { cleanSearchParamsFromUrl } from "@/common/utils/router.utils";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDown as ChevronDownIcon } from "@whoppah/icons/24/ChevronDown";

/**
 * Header language switcher.
 */
const HeaderLanguageSwitcher = ({ focused = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  const router = useRouter();
  const { isLoggedIn } = useAuth();
  const { urls } = useHeaderLanguageSwitcherContext();
  const { links } = useLocaleLinks(urls);

  const openMenu = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleLanguageChange =
    (locale: Locale) => (evt: React.MouseEvent<HTMLAnchorElement>) => {
      evt.stopPropagation();
      closeMenu();
      setLocaleCookie(locale);

      if (!isLoggedIn) {
        return;
      }

      if (router.locale === locale) {
        evt.preventDefault();
      }
    };

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger
        className={cn(
          "group cursor-pointer pb-4 font-title text-sm font-bold uppercase",
          focused && "mt-4"
        )}
        onMouseEnter={openMenu}
        onMouseLeave={closeMenu}
      >
        <span className="flex group-hover:text-attention-500">
          <span>{router.locale}</span>
          <ChevronDownIcon className="h-5 w-5" />
        </span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="center"
          onOpenAutoFocus={event => event.preventDefault()}
          onMouseEnter={openMenu}
          onMouseLeave={closeMenu}
          className="z-[100] data-[state=closed]:animate-menu-hide data-[state=open]:animate-menu-show"
        >
          <ul className="flex flex-col space-y-2 border bg-white p-5">
            {links.map(({ locale, path, sourceLocale }) => (
              <li
                key={locale}
                className="font-title text-sm capitalize hover:text-attention-500"
              >
                <Link
                  href={cleanSearchParamsFromUrl(path)}
                  locale={locale}
                  sourceLocale={sourceLocale}
                  prefetch={false}
                  className={
                    router.locale === locale
                      ? "font-bold text-attention-500"
                      : "font-normal"
                  }
                  lang={locale}
                  hrefLang={locale}
                  onClick={handleLanguageChange(locale)}
                >
                  {getLanguageName(locale)}
                </Link>
              </li>
            ))}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default HeaderLanguageSwitcher;
